import { isEqual } from 'lodash-es';

export type ResolvedChanges<T> = {
  toAddLocally: T[];
  toAddRemotely: T[];
  toUpdateLocally: T[];
  toUpdateRemotely: T[];
  toDeleteLocally: T[];
  toDeleteRemotely: T[];
};

export interface SyncableItem {
  id: string;
  updatedAt: number;
  archivedAt: number;
}

const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Pure last-write-wins conflict resolution between remote and local change
 * sets. Items archived more than a week ago are hard-deleted on both sides;
 * otherwise the newer updatedAt wins. `now` is injectable for tests.
 */
export function resolveConflicts<T extends SyncableItem>(
  remoteChanges: T[],
  localChanges: T[],
  now: number = Date.now()
): ResolvedChanges<T> {
  const resolvedChanges: ResolvedChanges<T> = {
    toAddLocally: [],
    toUpdateLocally: [],
    toDeleteLocally: [],
    toAddRemotely: [],
    toUpdateRemotely: [],
    toDeleteRemotely: [],
  };

  const oneWeekAgo = now - ONE_WEEK_MS;

  // Create maps for faster lookups
  const remoteMap = new Map(remoteChanges.map(item => [item.id, item]));
  const localMap = new Map(localChanges.map(item => [item.id, item]));

  // Process remote changes
  for (const remoteItem of remoteChanges) {
    const localItem = localMap.get(remoteItem.id);

    // Handle deletions first
    if (remoteItem.archivedAt && remoteItem.archivedAt < oneWeekAgo) {
      resolvedChanges.toDeleteLocally.push(remoteItem);
      resolvedChanges.toDeleteRemotely.push(remoteItem);
      continue;
    }

    // Skip if items are identical
    if (isEqual(remoteItem, localItem)) {
      continue;
    }

    if (!localItem) {
      // New remote item
      resolvedChanges.toAddLocally.push(remoteItem);
    } else {
      // Conflict resolution based on updatedAt timestamp
      if (remoteItem.updatedAt > localItem.updatedAt) {
        resolvedChanges.toUpdateLocally.push(remoteItem);
      }
    }
  }

  // Process local changes
  for (const localItem of localChanges) {
    const remoteItem = remoteMap.get(localItem.id);

    // Handle deletions first
    if (localItem.archivedAt && localItem.archivedAt < oneWeekAgo) {
      resolvedChanges.toDeleteRemotely.push(localItem);
      resolvedChanges.toDeleteLocally.push(localItem);
      continue;
    }

    if (isEqual(remoteItem, localItem)) {
      continue;
    }

    if (!remoteItem) {
      // New local item
      resolvedChanges.toAddRemotely.push(localItem);
    } else {
      // Conflict resolution based on updatedAt timestamp
      if (localItem.updatedAt > remoteItem.updatedAt) {
        resolvedChanges.toUpdateRemotely.push(localItem);
      }
    }
  }

  // Deduplicate arrays to prevent any potential double-processing
  const deduplicate = (array: T[]) =>
    Array.from(new Map(array.map(item => [item.id, item])).values());

  return {
    toAddLocally: deduplicate(resolvedChanges.toAddLocally),
    toAddRemotely: deduplicate(resolvedChanges.toAddRemotely),
    toUpdateLocally: deduplicate(resolvedChanges.toUpdateLocally),
    toUpdateRemotely: deduplicate(resolvedChanges.toUpdateRemotely),
    toDeleteLocally: deduplicate(resolvedChanges.toDeleteLocally),
    toDeleteRemotely: deduplicate(resolvedChanges.toDeleteRemotely),
  };
}
